// =============================================================================
// ClickUp Sidebar - Sidebar Projets avec charte couleurs ERP
// Utilise le Design System centralisé
// Mode collapsed: ⌘K, Favoris, Espaces, Équipe, Rôles, Inviter, Mise à jour
// =============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { cn } from '@/lib/utils';
import { getEmptyStateReason } from '@/lib/empty-state-utils';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, ChevronRight, ChevronLeft, Home, Star, FolderKanban, Plus, Settings, Search, Hash, Users, FileText, Shield, ListTodo, MoreHorizontal, Command, Layers, UserPlus, RefreshCw, Send, Loader2, ChevronsUp, Folder, Pencil, Trash2, Share2, Lock, Archive, Link2, Globe } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useFavorites } from '@/hooks/useFavorites';
import { useSidebarProjectsStore, useCollaborationStore } from '@/modules/project/hooks';
import { useSpacesStore } from '@/modules/project/hooks/useSpacesStore';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { SpaceMembersDialog } from '@/components/projects/SpaceMembersDialog';
import { CreateProjectDialog } from '@/components/projects/CreateProjectDialog';
import { CreateSpaceDialog } from '@/components/projects/CreateSpaceDialog';
import { EditSpaceDialog } from '@/components/projects/EditSpaceDialog';
import { EmptyStateByShareOnly } from '@/components/shared/EmptyStateByShareOnly';
import type { Space } from '@/modules/project/data/spaceRepository';
import { updateProject, deleteProject } from '@/modules/project/data/projectRepository';
import { SharePopover } from '@/components/layout/SharePopover';
import type { Project } from '@/types/project';
import type { AppRole } from '@/modules/core/access-control/definitions';
import { useIsGuest } from '@/contexts/GuestContext';
import { ERP_COLORS, MODULE_COLORS, TOOLTIP_STYLES } from '@/config/design-system';

// Alias pour la compatibilité avec le code existant
const COLORS = {
  ...ERP_COLORS,
  moduleColor: MODULE_COLORS.projects
};
interface ClickUpSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onOpenCommandPalette?: () => void;
  userRole?: AppRole | null;
}
export function ClickUpSidebar({
  collapsed,
  onToggle,
  onOpenCommandPalette,
  userRole = null
}: ClickUpSidebarProps) {
  const isGuest = useIsGuest();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    organizations,
    currentOrganization,
    setCurrentOrganization
  } = useOrganization();
  const {
    favorites,
    toggleFavorite,
    isFavorite
  } = useFavorites();
  const {
    user
  } = useAuth();
  const {
    projects,
    taskCounts,
    loadSidebarData,
    loading: projectsLoading,
    initialized: projectsInitialized,
    reorderOrMoveProject,
  } = useSidebarProjectsStore();
  const {
    spaces,
    loadSpaces,
    loading: spacesLoading,
    expandedSpaces: spacesExpanded,
    toggleSpaceExpanded
  } = useSpacesStore();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    espaces: true,
    favoris: true,
    unassigned: true
  });

  // Create space dialog state
  const [createSpaceDialogOpen, setCreateSpaceDialogOpen] = useState(false);
  
  // Edit space dialog state
  const [editSpaceDialogOpen, setEditSpaceDialogOpen] = useState(false);
  const [spaceToEdit, setSpaceToEdit] = useState<Space | null>(null);
  
  // Space members dialog state
  const [spaceMembersDialogOpen, setSpaceMembersDialogOpen] = useState(false);
  const [spaceToShare, setSpaceToShare] = useState<Space | null>(null);
  
  // Project context menu actions
  const [archiveConfirmProject, setArchiveConfirmProject] = useState<Project | null>(null);
  const [deleteConfirmProject, setDeleteConfirmProject] = useState<Project | null>(null);
  const [shareProject, setShareProject] = useState<Project | null>(null);

  // Track which space we're creating a project in
  const [createProjectSpaceId, setCreateProjectSpaceId] = useState<string | null>(null);

  // Extract projectId from current URL if on a project page
  const currentProjectId = useMemo(() => {
    const match = matchPath('/projects/:id/*', location.pathname) || matchPath('/projects/:id', location.pathname);
    return match?.params?.id || null;
  }, [location.pathname]);

  // Use collaboration store for project member management
  const collaboration = useCollaborationStore(currentProjectId || undefined, currentOrganization?.id, user?.id);

  // Create project dialog state
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const canManage = userRole === 'owner' || userRole === 'admin';
  const canManageProjectMembers = collaboration.canManageMembers || canManage;

  // Load sidebar data and spaces
  useEffect(() => {
    if (currentOrganization) {
      loadSidebarData(currentOrganization.id);
      loadSpaces(currentOrganization.id);
    }
  }, [currentOrganization?.id, loadSidebarData, loadSpaces]);

  // Group projects by space
  const projectsBySpace = useMemo(() => {
    const grouped: Record<string, typeof projects> = {};
    const unassigned: typeof projects = [];
    
    projects.forEach(project => {
      if (project.space_id) {
        if (!grouped[project.space_id]) {
          grouped[project.space_id] = [];
        }
        grouped[project.space_id].push(project);
      } else {
        unassigned.push(project);
      }
    });
    
    return { grouped, unassigned };
  }, [projects]);

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isActive = (path: string) => location.pathname === path;
  const isProjectActive = (projectId: string) => location.pathname === `/projects/${projectId}`;

  // Drag & drop projects across spaces / inside the same space
  const handleProjectDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination || !currentOrganization) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const SPACE_PREFIX = 'space-';
    const UNASSIGNED_ID = 'unassigned';

    const parseDroppable = (id: string): string | null => {
      if (id === UNASSIGNED_ID) return null;
      if (id.startsWith(SPACE_PREFIX)) return id.slice(SPACE_PREFIX.length);
      return null;
    };

    const sourceSpaceId = parseDroppable(source.droppableId);
    const targetSpaceId = parseDroppable(destination.droppableId);
    const isCrossSpace = sourceSpaceId !== targetSpaceId;

    try {
      await reorderOrMoveProject(
        draggableId,
        isCrossSpace ? targetSpaceId : undefined,
        destination.index,
        currentOrganization.id,
      );
      if (isCrossSpace) {
        toast.success('Projet déplacé');
      }
    } catch {
      // Le store (useSidebarProjectsStore) affiche déjà un toast d'erreur
      // explicatif via showActionError(). Pas de double notification ici.
    }
  };

  const handleOpenCommandPalette = () => {
    if (onOpenCommandPalette) {
      onOpenCommandPalette();
    } else {
      const event = new KeyboardEvent('keydown', {
        key: 'k',
        metaKey: true,
        ctrlKey: true,
        bubbles: true
      });
      document.dispatchEvent(event);
    }
  };
  const handleUpgrade = () => {
    toast.info('Bientôt disponible', {
      description: 'La page des forfaits sera disponible prochainement.'
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // COLLAPSED MODE - Style unifié avec ModuleSidebar (Light theme)
  // ─────────────────────────────────────────────────────────────────────────
  if (collapsed) {
    return <>
        <aside className="flex flex-col w-12 shrink-0 border rounded-xl h-full" style={{
        backgroundColor: '#FFFFFF',
        borderColor: '#E2E8F0'
      }}>
          {/* Header with expand button */}
          <div className="h-14 flex items-center justify-center border-b" style={{
          borderColor: '#E2E8F0'
        }}>
            <Button variant="ghost" size="icon" className="w-8 h-8 hover:bg-[#E2E8F0]" onClick={onToggle}>
              <ChevronRight className="h-4 w-4" style={{
              color: '#64748B'
            }} />
            </Button>
          </div>

          {/* Top Icons */}
          <div className="flex-1 flex flex-col items-center py-3 gap-1">
            {/* 1. Recherche ⌘K - Gris par défaut */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors" style={{
                backgroundColor: 'transparent'
              }} onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#E2E8F0';
              }} onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }} onClick={handleOpenCommandPalette}>
                  <Command className="h-4 w-4" style={{
                  color: '#64748B'
                }} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" style={{
              backgroundColor: '#1E293B',
              color: '#FFFFFF',
              borderColor: '#334155'
            }}>
                Recherche ⌘K
              </TooltipContent>
            </Tooltip>

            {/* 2. Favoris (dropdown) - Toujours jaune */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors" style={{
                backgroundColor: 'transparent'
              }} onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#E2E8F0';
              }} onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}>
                  <Star className="h-4 w-4" style={{
                  color: '#FBBF24'
                }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="right" sideOffset={8} className="w-56" style={{
              backgroundColor: '#FFFFFF',
              borderColor: '#E2E8F0'
            }}>
                <DropdownMenuLabel className="flex items-center gap-2" style={{
                color: '#64748B'
              }}>
                  <Star className="h-3 w-3" style={{
                  color: '#FBBF24'
                }} />
                  Projets favoris
                </DropdownMenuLabel>
                <DropdownMenuSeparator style={{
                backgroundColor: '#E2E8F0'
              }} />
                {favorites.length === 0 ? <div className="px-3 py-4 text-center text-sm" style={{
                color: '#64748B'
              }}>
                    Aucun favori
                  </div> : favorites.map(fav => <DropdownMenuItem key={fav.project_id} className="cursor-pointer" onClick={() => navigate(`/projects/${fav.project_id}`)}>
                      <div className="w-5 h-5 rounded flex items-center justify-center mr-2 shrink-0" style={{
                  backgroundColor: COLORS.moduleColor
                }}>
                        <span className="text-[9px] text-white font-bold">{fav.project_name?.[0]?.toUpperCase() || 'P'}</span>
                      </div>
                      <span className="truncate" style={{
                  color: '#0F172A'
                }}>{fav.project_name}</span>
                    </DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 3. Espaces/Projets (dropdown) - Gris par défaut */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors" style={{
                backgroundColor: 'transparent'
              }} onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#E2E8F0';
              }} onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}>
                  <Layers className="h-4 w-4" style={{
                  color: '#64748B'
                }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="right" sideOffset={8} className="w-64 max-h-80 overflow-y-auto" style={{
              backgroundColor: '#FFFFFF',
              borderColor: '#E2E8F0'
            }}>
                <DropdownMenuLabel className="flex items-center gap-2" style={{
                color: '#64748B'
              }}>
                  <Layers className="h-3 w-3" style={{
                  color: COLORS.moduleColor
                }} />
                  Tous les projets
                </DropdownMenuLabel>
                <DropdownMenuSeparator style={{
                backgroundColor: '#E2E8F0'
              }} />
                {projects.length === 0 ? <div className="px-3 py-4 text-center text-sm" style={{
                color: '#64748B'
              }}>
                    Aucun projet
                  </div> : projects.map(project => <DropdownMenuItem key={project.id} className="cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                      <div className="w-5 h-5 rounded flex items-center justify-center mr-2 shrink-0" style={{
                  backgroundColor: COLORS.moduleColor
                }}>
                        <span className="text-[9px] text-white font-bold">{project.name?.[0]?.toUpperCase() || 'P'}</span>
                      </div>
                      <span className="truncate flex-1" style={{
                  color: '#0F172A'
                }}>{project.name}</span>
                      <FolderKanban className="h-3.5 w-3.5 ml-2" style={{
                  color: '#64748B'
                }} />
                    </DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

      </>;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // EXPANDED MODE
  // ─────────────────────────────────────────────────────────────────────────

  const handleOrgSwitch = (org: typeof currentOrganization) => {
    if (!org || org.id === currentOrganization?.id) return;
    setCurrentOrganization(org);
    toast.success(`Workspace changé`, {
      description: `Vous êtes maintenant sur "${org.name}"`,
      duration: 3000
    });
    navigate('/dashboard', { replace: true });
  };

  // Composant réutilisable pour les items de navigation
  const NavItem = ({
    to,
    icon: Icon,
    label,
    active,
    onClick,
    className = '',
    iconClassName = ''
  }: {
    to?: string;
    icon: React.ComponentType<{
      className?: string;
      style?: React.CSSProperties;
    }>;
    label: string;
    active?: boolean;
    onClick?: () => void;
    className?: string;
    iconClassName?: string;
  }) => {
    const content = <>
        <Icon className={cn("h-4 w-4", iconClassName)} style={{
        color: active ? COLORS.moduleColor : COLORS.iconDefault
      }} />
        <span style={{
        color: active ? COLORS.textPrimary : COLORS.textInactive
      }}>
          {label}
        </span>
      </>;
    const baseStyle: React.CSSProperties = {
      backgroundColor: active ? COLORS.bgActive : 'transparent'
    };
    const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
      if (!active) {
        e.currentTarget.style.backgroundColor = COLORS.bgHover;
        const icon = e.currentTarget.querySelector('svg') as SVGElement | null;
        if (icon) icon.style.color = COLORS.iconHover;
        const span = e.currentTarget.querySelector('span') as HTMLElement | null;
        if (span) span.style.color = COLORS.textPrimary;
      }
    };
    const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
      if (!active) {
        e.currentTarget.style.backgroundColor = 'transparent';
        const icon = e.currentTarget.querySelector('svg') as SVGElement | null;
        if (icon) icon.style.color = COLORS.iconDefault;
        const span = e.currentTarget.querySelector('span') as HTMLElement | null;
        if (span) span.style.color = COLORS.textInactive;
      }
    };
    if (to) {
      return <NavLink to={to} className={cn('flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors', active && 'font-medium', className)} style={baseStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {content}
        </NavLink>;
    }
    return <button type="button" className={cn('flex items-center gap-2 px-3 py-2 rounded-lg text-sm w-full transition-colors', className)} style={baseStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={onClick}>
        {content}
      </button>;
  };
  return <aside data-tour="projects-sidebar" className="flex flex-col w-64 max-w-64 shrink-0 border rounded-xl h-full" style={{
    backgroundColor: '#FFFFFF',
    borderColor: '#E2E8F0',
    overflow: 'hidden'
  }}>
      {/* Header with Module Title */}
      <div className="h-14 flex items-center justify-between px-4 border-b shrink-0" style={{
      borderColor: '#E2E8F0'
    }}>
        <div className="flex items-center gap-2">
          <FolderKanban className="h-5 w-5" style={{
          color: COLORS.moduleColor
        }} />
          <span className="font-semibold text-sm" style={{
          color: COLORS.textPrimary
        }}>
            Projets
          </span>
        </div>
        <button className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0 transition-colors" style={{
        backgroundColor: 'transparent'
      }} onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = COLORS.bgHover;
      }} onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }} onClick={onToggle}>
          <ChevronLeft className="h-4 w-4" style={{
          color: COLORS.iconDefault
        }} />
        </button>
      </div>


      <ScrollArea className="flex-1 min-w-0">
        <div className="px-3 py-2 space-y-1 max-w-[232px]">
          {/* Search - Style amélioré avec fond visible */}
          <button type="button" className="w-full flex items-center gap-1.5 px-2 py-2 rounded-lg text-sm transition-all border box-border" style={{
          backgroundColor: '#FFFFFF',
          borderColor: '#FFFFFF',
          color: COLORS.textMuted
        }} onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = '#F1F5F9';
          e.currentTarget.style.borderColor = COLORS.moduleColor;
          e.currentTarget.style.color = COLORS.textPrimary;
        }} onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = '#FFFFFF';
          e.currentTarget.style.borderColor = '#FFFFFF';
          e.currentTarget.style.color = COLORS.textMuted;
        }} onClick={() => {
          const event = new KeyboardEvent('keydown', {
            key: 'k',
            metaKey: true
          });
          document.dispatchEvent(event);
        }}>
            <Search className="h-3.5 w-3.5 shrink-0" style={{
            color: COLORS.iconDefault
          }} />
            <span className="flex-1 text-left text-xs truncate min-w-0">Rechercher</span>
            <kbd className="text-[10px] rounded px-1 py-0.5 font-mono shrink-0 whitespace-nowrap" style={{
            backgroundColor: '#FFFFFF',
            color: COLORS.textSecondary,
            border: `1px solid ${COLORS.border}`
          }}>
              ⌘K
            </kbd>
          </button>

          {/* Favoris */}
          <div className="pt-4">
            <button type="button" className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-medium uppercase tracking-wider" style={{
              color: COLORS.textMuted
            }} onClick={() => toggleSection('favoris')}>
              {expandedSections['favoris'] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              <Star className="h-3 w-3" />
              Favoris
            </button>
            {expandedSections['favoris'] && <div className="ml-4 mt-1 space-y-0.5">
              {favorites.length === 0 ? <div className="px-2 py-2">
                <p className="text-xs leading-relaxed" style={{ color: COLORS.textMuted }}>
                  Cliquez sur <Star className="inline h-3 w-3 text-yellow-500 fill-yellow-500 mx-0.5" /> pour ajouter des favoris à votre barre latérale.
                </p>
              </div> : favorites.map(fav => {
                const active = location.pathname === `/projects/${fav.project_id}`;
                return <NavLink key={fav.project_id} to={`/projects/${fav.project_id}`} className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg group transition-colors min-w-0" style={{
                  backgroundColor: active ? COLORS.bgActive : 'transparent',
                  color: active ? COLORS.textPrimary : COLORS.textInactive
                }} onMouseEnter={e => {
                  if (!active) e.currentTarget.style.backgroundColor = COLORS.bgHover;
                }} onMouseLeave={e => {
                  if (!active) e.currentTarget.style.backgroundColor = 'transparent';
                }}>
                                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 shrink-0" />
                                  <span className="overflow-hidden text-ellipsis whitespace-nowrap" style={{ flex: '1 1 auto', minWidth: 0 }}>{fav.project_name}</span>
                  <button type="button" onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleFavorite(fav.project_id, fav.project_name);
                  }} className="opacity-0 group-hover:opacity-100" style={{ color: COLORS.textMuted }}>
                    <MoreHorizontal className="h-3 w-3" />
                  </button>
                </NavLink>;
              })}
            </div>}
          </div>

          {/* Espaces */}
          <div className="pt-4">
            <div className="flex items-center justify-between px-2 py-1.5">
              <button type="button" className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider" style={{
                color: COLORS.textMuted
              }} onClick={() => toggleSection('espaces')}>
                {expandedSections['espaces'] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Espaces
              </button>
              {!isGuest && (
                <button className="h-5 w-5 rounded flex items-center justify-center transition-colors" style={{
                  color: COLORS.textMuted
                }} onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = COLORS.bgHover;
                  e.currentTarget.style.color = COLORS.textPrimary;
                }} onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = COLORS.textMuted;
                }} onClick={() => setCreateSpaceDialogOpen(true)}>
                  <Plus className="h-3 w-3" />
                </button>
              )}
            </div>

            {expandedSections['espaces'] && <DragDropContext onDragEnd={handleProjectDragEnd}>
              <div className="mt-1 space-y-0.5">
              {/* Dynamic Spaces */}
              {spaces.map(space => {
                const spaceProjects = projectsBySpace.grouped[space.id] || [];
                const isSpaceExpanded = spacesExpanded.has(space.id);
                // For the default space, also include unassigned projects
                const displayProjects = space.is_default 
                  ? [...spaceProjects, ...projectsBySpace.unassigned]
                  : spaceProjects;
                
                return <div key={space.id} className="group/space">
                  <div className="flex items-center">
                    <button type="button" className="flex-1 flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg transition-colors min-w-0" style={{
                      color: COLORS.textInactive
                    }} onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = COLORS.bgHover;
                    }} onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }} onClick={() => toggleSpaceExpanded(space.id)}>
                      {isSpaceExpanded ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
                      <div className="w-5 h-5 rounded flex items-center justify-center shrink-0" style={{
                        backgroundColor: space.color || COLORS.moduleColor
                      }}>
                        <span className="text-[10px] text-white font-medium">{space.name[0]?.toUpperCase()}</span>
                      </div>
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap text-left" style={{ flex: '1 1 0', minWidth: 0 }}>{space.name}</span>
                      {space.visibility === 'private' && (
                        <Lock className="h-3 w-3 shrink-0" style={{ color: '#F59E0B' }} />
                      )}
                      <span className="text-xs px-1.5 py-0.5 rounded-full shrink-0 group-hover/space:hidden" style={{ 
                        backgroundColor: displayProjects.length > 0 ? COLORS.bgActive : 'transparent',
                        color: COLORS.textMuted 
                      }}>
                        {displayProjects.length}
                      </span>
                    </button>
                    
                    {/* Space context menu - hidden for guests */}
                    {!isGuest && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button 
                          type="button" 
                          className="opacity-0 pointer-events-none group-hover/space:opacity-100 group-hover/space:pointer-events-auto flex items-center justify-center p-1 rounded transition-colors mr-1 shrink-0" 
                          style={{ color: COLORS.textMuted }} 
                          onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = COLORS.bgHover;
                          }} 
                          onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-3 w-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => {
                          setSpaceToShare(space);
                          setSpaceMembersDialogOpen(true);
                        }}>
                          <Share2 className="h-4 w-4 mr-2" />
                          Partager l'espace
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => {
                          setSpaceToEdit(space);
                          setEditSpaceDialogOpen(true);
                        }}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Modifier l'espace
                        </DropdownMenuItem>
                        {!space.is_default && !space.is_archive && (
                          <DropdownMenuItem 
                            className="text-destructive focus:text-destructive"
                            onClick={() => {
                              setSpaceToEdit(space);
                              setEditSpaceDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer l'espace
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    )}
                  </div>
                  
                  {isSpaceExpanded && <div className="ml-4 mt-0.5 space-y-0.5">
                    {/* Projects sub-section */}
                    <div className="flex items-center gap-2 px-2 py-1 text-xs font-medium" style={{ color: COLORS.textMuted }}>
                      <FolderKanban className="h-3.5 w-3.5" style={{ color: COLORS.moduleColor }} />
                      <span>Projets</span>
                      <span className="ml-auto text-[10px]">{displayProjects.length}</span>
                    </div>
                    
                    <div className="ml-2 space-y-0.5">
                      <Droppable
                        droppableId={`space-${space.id}`}
                        type="PROJECT"
                        isDropDisabled={!!space.is_archive || isGuest}
                      >
                        {(dropProvided, dropSnapshot) => (
                          <div
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            className={cn(
                              "space-y-0.5 rounded-md transition-colors",
                              displayProjects.length === 0 && "min-h-[44px] border border-dashed",
                              dropSnapshot.isDraggingOver && "bg-[hsl(var(--module-primary)/0.08)] border-[hsl(var(--module-primary)/0.4)]",
                              displayProjects.length === 0 && !dropSnapshot.isDraggingOver && "border-transparent"
                            )}
                          >
                      {displayProjects.map((project, projectIndex) => {
                        const active = isProjectActive(project.id);
                        const hasFav = isFavorite(project.id);
                        const count = taskCounts[project.id] || 0;
                        const isArchivedProject = project.status === 'archived' || !!project.original_space_id || space.is_archive;
                        const isDragDisabled = isGuest || isArchivedProject;
                        return <Draggable
                          key={project.id}
                          draggableId={project.id}
                          index={projectIndex}
                          isDragDisabled={isDragDisabled}
                        >
                          {(dragProvided, dragSnapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            className={cn("group relative", dragSnapshot.isDragging && "opacity-90 ring-1 ring-primary/40 rounded-lg bg-background shadow-sm")}
                            style={dragProvided.draggableProps.style}
                          >
                          <NavLink to={`/projects/${project.id}`} className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg transition-colors pr-16" style={{
                            backgroundColor: active ? COLORS.bgActive : 'transparent',
                            color: active ? COLORS.textPrimary : COLORS.textInactive
                          }} onMouseEnter={e => {
                            if (!active) e.currentTarget.style.backgroundColor = COLORS.bgHover;
                          }} onMouseLeave={e => {
                            if (!active) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          data-testid="sidebar-project-link"
                          data-project-id={project.id}
                          data-project-name={project.name}
                          >
                            <Hash className="h-3 w-3 shrink-0" style={{ color: active ? COLORS.moduleColor : COLORS.iconDefault }} />
                            <span className="flex-1 min-w-0 truncate">{project.name}</span>
                            {project.status === 'completed' && (
                              <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" title="Terminé" />
                            )}
                            {project.status === 'on_hold' && (
                              <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" title="En pause" />
                            )}
                            {project.visibility === 'private' && (
                              <Lock className="h-3 w-3 shrink-0 text-amber-500" />
                            )}
                          </NavLink>
                          {/* Right side icons - absolute positioned */}
                          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
                            {count > 0 && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full min-w-[18px] text-center" style={{
                              backgroundColor: COLORS.bgIconNeutral,
                              color: COLORS.textSecondary
                            }}>
                              {count}
                            </span>}
                            {hasFav && <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />}
                            {!isGuest && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button type="button" className="opacity-0 group-hover:opacity-100 p-1 rounded transition-colors" style={{ color: COLORS.textMuted }} onMouseEnter={e => {
                                  e.currentTarget.style.backgroundColor = COLORS.bgHover;
                                }} onMouseLeave={e => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}>
                                  <MoreHorizontal className="h-3 w-3" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent side="right" align="start" sideOffset={4} className="w-56 p-1">
                                {/* Section 1: Favori & Renommer & Copier le lien */}
                                <DropdownMenuItem onClick={() => toggleFavorite(project.id, project.name)}>
                                  <Star className={cn("h-4 w-4 mr-2", hasFav && "text-yellow-500 fill-yellow-500")} />
                                  {hasFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  const isProduction = window.location.hostname === 'app.lapyno.com' || window.location.hostname === 'staging.app.lapyno.com';
                                  const baseUrl = isProduction ? window.location.origin : 'https://app.lapyno.com';
                                  navigator.clipboard.writeText(`${baseUrl}/projects/${project.id}`);
                                  toast.success('Lien copié dans le presse-papiers');
                                }}>
                                  <Link2 className="h-4 w-4 mr-2" />
                                  Copier le lien
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {/* Section 2: Paramètres */}
                                <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}?settings=true`)}>
                                  <Settings className="h-4 w-4 mr-2" />
                                  Paramètres
                                </DropdownMenuItem>
                                {(() => {
                                  const canToggleVisibility = canManage || (project.created_by === user?.id);
                                  return (
                                    <DropdownMenuItem 
                                      onClick={canToggleVisibility ? async () => {
                                        const newVisibility = project.visibility === 'private' ? 'public' : 'private';
                                        await updateProject(project.id, { visibility: newVisibility }, user?.id || undefined);
                                        if (currentOrganization) {
                                          await loadSidebarData(currentOrganization.id);
                                        }
                                        toast.success(newVisibility === 'private' ? 'Projet rendu privé' : 'Projet rendu public');
                                      } : undefined}
                                      disabled={!canToggleVisibility}
                                      className={!canToggleVisibility ? 'opacity-50 cursor-not-allowed' : ''}
                                    >
                                      {!canToggleVisibility ? (
                                        <Lock className="h-4 w-4 mr-2 text-muted-foreground" />
                                      ) : project.visibility === 'private' ? (
                                        <Globe className="h-4 w-4 mr-2" />
                                      ) : (
                                        <Lock className="h-4 w-4 mr-2" />
                                      )}
                                      {project.visibility === 'private' ? 'Rendre public' : 'Rendre privé'}
                                    </DropdownMenuItem>
                                  );
                                })()}
                                <DropdownMenuSeparator />
                                {/* Section 3: Archiver & Supprimer */}
                                <DropdownMenuItem 
                                  onClick={canManage ? () => setArchiveConfirmProject(project) : undefined}
                                  disabled={!canManage}
                                  className={!canManage ? 'opacity-50 cursor-not-allowed' : ''}
                                >
                                  {!canManage ? <Lock className="h-4 w-4 mr-2 text-muted-foreground" /> : <Archive className="h-4 w-4 mr-2" />}
                                  {isArchivedProject ? 'Désarchiver' : 'Archiver'}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className={!canManage ? 'opacity-50 cursor-not-allowed' : 'text-destructive focus:text-destructive'}
                                  onClick={canManage ? () => setDeleteConfirmProject(project) : undefined}
                                  disabled={!canManage}
                                >
                                  {!canManage ? <Lock className="h-4 w-4 mr-2 text-muted-foreground" /> : <Trash2 className="h-4 w-4 mr-2" />}
                                  Supprimer
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {/* Section 4: Partage - styled like ClickUp */}
                                <DropdownMenuItem 
                                  className="bg-[hsl(var(--module-primary))] text-white rounded-md mx-0.5 my-0.5 focus:bg-[hsl(var(--module-primary)/0.9)] focus:text-white justify-center font-medium"
                                  onClick={() => navigate(`/projects/${project.id}?settings=true&tab=team`)}
                                >
                                  Partage et permissions
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            )}
                          </div>
                          </div>
                          )}
                        </Draggable>;
                      })}
                          {dropProvided.placeholder}
                          {displayProjects.length === 0 && !space.is_archive && (
                            <div
                              className="px-2 py-2 text-[11px] italic text-center pointer-events-none"
                              style={{ color: COLORS.textMuted }}
                            >
                              {dropSnapshot.isDraggingOver ? 'Déposer ici' : 'Aucun projet — glisser ici'}
                            </div>
                          )}
                          </div>
                        )}
                      </Droppable>
                      {/* Don't show "New project" button in archive space */}
                      {!space.is_archive && (
                        <button type="button" className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg transition-colors" style={{
                          color: COLORS.textMuted
                        }} onMouseEnter={e => {
                          e.currentTarget.style.backgroundColor = COLORS.bgHover;
                          e.currentTarget.style.color = COLORS.textPrimary;
                        }} onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = COLORS.textMuted;
                        }} onClick={() => {
                          setCreateProjectSpaceId(space.id);
                          setCreateProjectDialogOpen(true);
                        }}
                        data-tour="create-project"
                        data-testid="sidebar-create-project-trigger"
                        >
                          <Plus className="h-3 w-3" />
                          <span>Nouveau projet</span>
                        </button>
                      )}
                    </div>
                  </div>}
                </div>;
              })}

              {/* Fallback for orgs without default space - show unassigned projects if any space exists but has no default */}
              {spaces.length > 0 && !spaces.some(s => s.is_default) && projectsBySpace.unassigned.length > 0 && <>
                <button type="button" className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg transition-colors" style={{
                  color: COLORS.textInactive
                }} onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = COLORS.bgHover;
                }} onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }} onClick={() => toggleSection('unassigned')}>
                  {expandedSections['unassigned'] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  <FolderKanban className="h-4 w-4" style={{ color: COLORS.iconDefault }} />
                  <span className="flex-1 text-left">Projets</span>
                  <span className="text-xs" style={{ color: COLORS.textMuted }}>
                    {projectsBySpace.unassigned.length}
                  </span>
                </button>

                {expandedSections['unassigned'] && <div className="ml-4 mt-0.5 space-y-0.5">
                  {projectsBySpace.unassigned.map(project => {
                    const active = isProjectActive(project.id);
                    return <div key={project.id} className="group relative">
                      <div className="flex items-center min-w-0 group relative">
                        <NavLink to={`/projects/${project.id}`} className="flex-1 flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg transition-colors overflow-hidden" style={{
                          backgroundColor: active ? COLORS.bgActive : 'transparent',
                          color: active ? COLORS.textPrimary : COLORS.textInactive
                        }} onMouseEnter={e => {
                          if (!active) e.currentTarget.style.backgroundColor = COLORS.bgHover;
                        }} onMouseLeave={e => {
                          if (!active) e.currentTarget.style.backgroundColor = 'transparent';
                        }}>
                          <Hash className="h-3 w-3 shrink-0" style={{ color: active ? COLORS.moduleColor : COLORS.iconDefault }} />
                          <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{project.name}</span>
                          {taskCounts[project.id] > 0 && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full min-w-[18px] text-center shrink-0" style={{
                            backgroundColor: COLORS.bgIconNeutral,
                            color: COLORS.textSecondary
                          }}>
                            {taskCounts[project.id]}
                          </span>}
                          {isFavorite(project.id) && <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 shrink-0" />}
                        </NavLink>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button type="button" className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 rounded transition-colors z-10" style={{ color: COLORS.textMuted }} onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = COLORS.bgHover;
                            }} onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}>
                              <MoreHorizontal className="h-3 w-3" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => toggleFavorite(project.id, project.name)}>
                              <Star className={cn("h-4 w-4 mr-2", isFavorite(project.id) && "text-yellow-500 fill-yellow-500")} />
                              {isFavorite(project.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>;
                  })}
                </div>}
              </>}

              {/* Skeleton loader during initial load */}
              {!projectsInitialized && (
                <div className="px-2 py-2 space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="space-y-1.5">
                      <Skeleton className="h-4 w-24 rounded" />
                      <div className="pl-3 space-y-1">
                        <Skeleton className="h-3.5 w-32 rounded" />
                        <Skeleton className="h-3.5 w-28 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state - by share only for member/guest */}
              {projectsInitialized && (() => {
                const reason = getEmptyStateReason({
                  spaces,
                  projects,
                  canManage,
                });
                if (reason) {
                  return (
                    <EmptyStateByShareOnly 
                      variant="compact" 
                      canManage={false}
                      reason={reason}
                    />
                  );
                }
                return null;
              })()}

              {/* Empty state - no spaces yet (for owner/admin) */}
              {spaces.length === 0 && projects.length === 0 && canManage && (
                <div className="px-2 py-3 text-center">
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>
                    Créez un espace pour organiser vos projets
                  </p>
                </div>
              )}
              </div>
            </DragDropContext>}
          </div>

        </div>

      </ScrollArea>

      {/* Create Project Dialog */}
      <CreateProjectDialog 
        open={createProjectDialogOpen} 
        onOpenChange={(open) => {
          setCreateProjectDialogOpen(open);
          if (!open) setCreateProjectSpaceId(null);
        }}
        defaultSpaceId={createProjectSpaceId}
      />

      {/* Create Space Dialog */}
      <CreateSpaceDialog
        open={createSpaceDialogOpen}
        onOpenChange={setCreateSpaceDialogOpen}
      />

      {/* Edit Space Dialog */}
      <EditSpaceDialog
        open={editSpaceDialogOpen}
        onOpenChange={setEditSpaceDialogOpen}
        space={spaceToEdit}
      />

      {/* Space Members Dialog */}
      {spaceToShare && currentOrganization && (
        <SpaceMembersDialog
          space={spaceToShare}
          orgId={currentOrganization.id}
          open={spaceMembersDialogOpen}
          onClose={() => {
            setSpaceMembersDialogOpen(false);
            setSpaceToShare(null);
          }}
          canManage={canManage}
        />
      )}

      {/* Archive Confirmation */}
      <AlertDialog open={!!archiveConfirmProject} onOpenChange={(open) => !open && setArchiveConfirmProject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[14px]">
              {(archiveConfirmProject?.status === 'archived' || !!archiveConfirmProject?.original_space_id) ? 'Désarchiver le projet ?' : 'Archiver le projet ?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[12px]">
              {(archiveConfirmProject?.status === 'archived' || !!archiveConfirmProject?.original_space_id)
                ? `Le projet "${archiveConfirmProject?.name}" sera restauré dans son espace d'origine.`
                : `Le projet "${archiveConfirmProject?.name}" sera déplacé dans les archives. Vous pourrez le restaurer plus tard.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-8 text-[12px]">Annuler</AlertDialogCancel>
            <AlertDialogAction className="h-8 text-[12px]" onClick={async () => {
              if (!archiveConfirmProject || !currentOrganization || !user) return;
              try {
                const isArchivedProject = archiveConfirmProject.status === 'archived' || !!archiveConfirmProject.original_space_id;
                const isArchiving = !isArchivedProject;
                await updateProject(archiveConfirmProject.id, {
                  status: isArchiving
                    ? 'archived'
                    : (archiveConfirmProject.status === 'archived' ? 'active' : archiveConfirmProject.status),
                } as any, user.id);
                toast.success(isArchiving ? 'Projet archivé' : 'Projet désarchivé');
                loadSidebarData(currentOrganization.id);
              } catch (err: any) {
                toast.error(err.message || 'Erreur');
              }
              setArchiveConfirmProject(null);
            }}>
              {(archiveConfirmProject?.status === 'archived' || !!archiveConfirmProject?.original_space_id) ? 'Désarchiver' : 'Archiver'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmProject} onOpenChange={(open) => !open && setDeleteConfirmProject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[14px]">Supprimer le projet ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[12px]">
              Le projet "{deleteConfirmProject?.name}" et toutes ses tâches seront supprimés définitivement. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-8 text-[12px]">Annuler</AlertDialogCancel>
            <AlertDialogAction className="h-8 text-[12px] bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={async () => {
              if (!deleteConfirmProject || !currentOrganization) return;
              try {
                await deleteProject(deleteConfirmProject.id);
                toast.success('Projet supprimé');
                loadSidebarData(currentOrganization.id);
                if (currentProjectId === deleteConfirmProject.id) {
                  navigate('/');
                }
              } catch (err: any) {
                toast.error(err.message || 'Erreur lors de la suppression');
              }
              setDeleteConfirmProject(null);
            }}>
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>;
}